import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Share2, Target, Code, Palette, Users, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import { services, stats, testimonials, companyInfo } from '../mockData';

const iconMap = {
  TrendingUp,
  Share2,
  Target,
  Code,
  Palette,
  Users
};

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Arikone - Digital Marketing Agency in Noida | Brand Promotion & Social Media Marketing"
        description="Arikone: Leading digital marketing agency in Noida offering brand promotion, social media marketing, performance ads, website development, and lead generation services across Delhi NCR."
        keywords="digital marketing agency noida, brand promotion noida, social media marketing delhi, performance ads, website development, branding agency noida, lead generation, digital marketing delhi NCR"
      />
      <StructuredData />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
        {/* Animated Creative Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Mesh Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

          {/* Glowing Orbs */}
          <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-500/30 rounded-full mix-blend-screen filter blur-[100px] animate-blob"></div>
          <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/30 rounded-full mix-blend-screen filter blur-[100px] animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-[600px] h-[600px] bg-indigo-500/20 rounded-full mix-blend-screen filter blur-[120px] animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          {/* Glassmorphic Container for Focus */}
          <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>

            {/* Moving Tagline - Content Preserved */}
            <div className="mb-10 overflow-hidden relative z-10">
              <div className="moving-tagline">
                <span className="text-2xl sm:text-3xl font-semibold tracking-wide">
                  <span className="text-white drop-shadow-md">Business</span>{' '}
                  <span className="text-red-400 drop-shadow-md">Apka</span>{' '}
                  <span className="text-yellow-400 drop-shadow-md">Saath</span>{' '}
                  <span className="text-green-400 drop-shadow-md">Hamara</span>
                  {' • '}
                  <span className="text-white drop-shadow-md">Business</span>{' '}
                  <span className="text-red-400 drop-shadow-md">Apka</span>{' '}
                  <span className="text-yellow-400 drop-shadow-md">Saath</span>{' '}
                  <span className="text-green-400 drop-shadow-md">Hamara</span>
                  {' • '}
                  <span className="text-white drop-shadow-md">Business</span>{' '}
                  <span className="text-red-400 drop-shadow-md">Apka</span>{' '}
                  <span className="text-yellow-400 drop-shadow-md">Saath</span>{' '}
                  <span className="text-green-400 drop-shadow-md">Hamara</span>
                  {' • '}
                </span>
              </div>
            </div>

            <div className={`space-y-8 relative z-10 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <h1 className="text-5xl sm:text-6xl lg:text-8xl font-bold text-white leading-tight tracking-tight">
                Empowering Your Brand to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 animate-gradient-x">
                  Thrive
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-blue-100 max-w-3xl mx-auto font-light leading-relaxed">
                {companyInfo.description.split('.')[0]}.
              </p>
              <div className="flex flex-col sm:flex-row gap-5 justify-center items-center pt-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-blue-900 hover:bg-blue-50 px-10 py-7 text-lg btn-primary rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all duration-300"
                >
                  <Link to="/contact">
                    Get Started <ArrowRight className="ml-2" size={20} />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white bg-transparent hover:bg-white/10 px-10 py-7 text-lg rounded-full backdrop-blur-md transition-all duration-300"
                >
                  <Link to="/portfolio">View Our Work</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Section - Updated for Dark Theme */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 relative z-10">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center animate-fadeInUp stagger-${index + 1} p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/5 hover:bg-white/10 transition-colors`}
              >
                <div className="text-4xl sm:text-5xl font-bold text-blue-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-blue-200 font-medium tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Our <span className="gradient-text">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive digital solutions tailored to elevate your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className={`group hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-0 overflow-hidden bg-white/80 backdrop-blur-sm animate-fadeInUp stagger-${index + 1}`}
                >
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 shadow-lg ${index % 3 === 0 ? 'bg-blue-100 text-blue-600 shadow-blue-200' :
                      index % 3 === 1 ? 'bg-purple-100 text-purple-600 shadow-purple-200' :
                        'bg-emerald-100 text-emerald-600 shadow-emerald-200'
                      }`}>
                      <IconComponent size={32} />
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-3 mb-6">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700 font-medium">
                          <CheckCircle className={`mr-2 w-4 h-4 ${index % 3 === 0 ? 'text-blue-500' :
                            index % 3 === 1 ? 'text-purple-500' :
                              'text-emerald-500'
                            }`} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="pt-4 border-t border-gray-100">
                      <span className="text-sm font-semibold text-gray-400 group-hover:text-gray-900 flex items-center transition-colors">
                        Learn More <ArrowRight className="ml-2 w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-16">
            <Button
              asChild
              size="lg"
              className="bg-gray-900 hover:bg-black text-white px-10 py-7 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105"
            >
              <Link to="/services">
                Explore All Services <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Arikone Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Why Choose Arikone?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Your trusted partner for digital growth and brand success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Proven Results',
                description: 'Track record of delivering measurable ROI and business growth for clients across industries.'
              },
              {
                title: 'Expert Team',
                description: '15+ years of combined experience in digital marketing, branding, and creative strategy.'
              },
              {
                title: 'Customized Solutions',
                description: 'Tailored strategies designed specifically for your business goals and target audience.'
              },
              {
                title: 'Data-Driven Approach',
                description: 'Strategic decisions backed by analytics and market insights for optimal performance.'
              },
              {
                title: 'Full-Service Agency',
                description: 'End-to-end solutions from strategy to execution, all under one roof.'
              },
              {
                title: 'Dedicated Support',
                description: 'Personalized attention and ongoing support to ensure your success.'
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-all duration-300 animate-fadeInUp stagger-${index + 1}`}
              >
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-blue-100">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real feedback from businesses we've helped grow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className={`animate-fadeInUp stagger-${index + 1}`}>
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-yellow-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Transform Your Brand?
          </h2>
          <p className="text-xl mb-8 text-blue-50">
            Let's discuss how we can help your business thrive in the digital landscape.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg btn-primary"
            >
              <Link to="/contact">Book a Free Consultation</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg btn-secondary"
            >
              <Link to="/portfolio">View Our Portfolio</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;